import type { ImageGravity, Sizing } from "common/components/image-types";
import { ImageAspectRatio, ImageFitStyle } from "common/components/image-types";
import { massageCloudURL } from "common/components/portable-renderers";
import type { AppEnvironment, UploadSession } from "common/components/types";
import { isUploadFileResponseError } from "common/components/types";
import { logError } from "common/debug-print";
import type { DropData } from "common/drop-controller";
import { extractDropData } from "common/drop-controller";
import { uploadCancelX } from "common/named-icons-lite";
import { isEmoji } from "common/Support";
import type { OnClickHandler } from "hooks/use-on-click-from-action";
import { ErrorBoundary } from "lib/error-boundary";
import { lazilyLoadedComponent } from "lib/react-lazy-loading";
import * as React from "react";
import { showCancelUploadModal, showUploadFailedRetryModal } from "webapp/lib/in-app-modals";
import { getFirstFileFromInput, readFileAsURL } from "../../../lib/file-utils";
import { ImagePlaceholder } from "../../../styled-components";
import AutoImage from "../../views/auto-image/auto-image";
import OverlayContainerView from "../../views/overlay-container/overlay-container-view";
import Carousel from "../carousel/carousel";
import DroppableDiv from "../droppable-div/droppable-div";
import {
    ImageCarouselProgressBar,
    ImageCarouselStyle,
    PlaceholderWrapper,
    StreamlineCamera,
} from "./image-carousel-style";

const ImageOverlayView = lazilyLoadedComponent(
    "image-overlay-view",
    () => import("../../views/image-overlay/image-overlay-view")
);

interface Props {
    readonly appEnvironment: AppEnvironment;
    readonly fitStyle: ImageFitStyle;
    readonly gravity: ImageGravity;
    readonly images: string[];
    readonly size: ImageAspectRatio;
    readonly sizing: Sizing;
    readonly tapToEnlarge: boolean;
    readonly uploadDisabled: boolean;
    readonly showUploadButton?: boolean;

    readonly onClickItem?: OnClickHandler;
    readonly onUploadEnd?: (index: number, url: string | undefined) => void;
    readonly onUploadStart?: (index: number) => void;

    readonly bottomLeftOverlay?: React.ReactNode;
    readonly bottomRightOverlay?: React.ReactNode;
    readonly topLeftOverlay?: React.ReactNode;
    readonly topRightOverlay?: React.ReactNode;
}

interface State {
    readonly index: number;
    readonly overlayShowing: boolean;
    readonly sourceOverrides: Record<number, string>;
    readonly uploadProgresses: Record<number, number>;
}

// In the past, this was a function component. But we grew
// a requirement that precludes our ability to use those:
//
// You need to be able to temporarily override the image source
// during uploads.
//
// The obvious way to implement this is Record<number, string>.
// You just map the index to the temporary URL. But this is
// actually incompatible with function components and the half-baked
// way hooks work vs the way state updates trigger renders.
//
// React performs an Object.is() on all of its state entries
// before every render of pure components; if the Object.is()
// call returns `true` then React just skips rendering. This means
// we have to reset our sourceOverrides with a copy, every time.
//
// That's not so bad, except React doesn't give us a way to arbitrarily
// poll for the current state in asynchronous code with hooks. All you get
// is a snapshot that was valid at the render time. If you allocate, then call
// async methods during the render then they'll close over a copy of the
// value in the hook state and can't poll for the actual state later on.
//
// We expect some async invocations to overlap in incompatible ways, it's
// just part of what we have to do. So not being able to get the _exact_
// current state for instance replacement to trigger a re-render is not
// going to work for us.
//
// PureComponent doesn't have this problem. We can always get the current
// value of the state at any given point in time.

// Ok, we _could_ kick a completely useless state random variable every
// time we want to render, but those contortions become real gross real fast.
//
// But that still doesn't matter because we need componentDidUpdate to prevent
// visual flakiness and object-local storage for upload sessions.
class ImageCarousel extends React.PureComponent<Props, State> {
    state: State = {
        index: 0,
        overlayShowing: false,
        sourceOverrides: {},
        uploadProgresses: {},
    };

    private uploadSessions: Record<number, UploadSession> = {};
    private inputRef = React.createRef<HTMLInputElement>();

    private onClick = async () => {
        const { onClickItem, tapToEnlarge } = this.props;

        if (tapToEnlarge) {
            this.setState({ overlayShowing: true });
        }
        await onClickItem?.call(undefined, false);
    };

    private setSourceOverride(overrideURL: string, index: number) {
        const { sourceOverrides } = this.state;
        this.setState({ sourceOverrides: { ...sourceOverrides, [index]: overrideURL } });
    }

    private clearSourceOverride(overrideURL: string, index: number) {
        const { sourceOverrides } = this.state;
        if (sourceOverrides[index] !== overrideURL) return;
        const replacement = { ...sourceOverrides };
        delete replacement[index];
        this.setState({ sourceOverrides: replacement });
    }

    private setUploadProgress(progress: number, index: number) {
        const { uploadProgresses } = this.state;
        this.setState({ uploadProgresses: { ...uploadProgresses, [index]: progress } });
    }

    private clearUploadProgress(index: number) {
        const { uploadProgresses } = this.state;
        const replacement = { ...uploadProgresses };
        delete replacement[index];
        this.setState({ uploadProgresses: replacement });
    }

    private reportProgress(index: number) {
        return (total: number, current: number) => {
            this.setUploadProgress((current * 100) / total, index);
        };
    }

    private onUploadCancelClick = async (ev: React.MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        ev.preventDefault();

        const shouldCancel = await showCancelUploadModal(this.props.appEnvironment.appKind);
        if (!shouldCancel) return;

        const { index } = this.state;
        const session = this.uploadSessions[index];
        if (session === undefined) return;
        session.cancel();
        delete this.uploadSessions[index];
    };

    private async handleFileUpload(file: File, index: number): Promise<void> {
        const { onUploadEnd, appEnvironment } = this.props;

        if (onUploadEnd === undefined) {
            return;
        }
        this.setUploadProgress(0, index);

        const priorSession = this.uploadSessions[index];
        if (priorSession !== undefined) {
            priorSession.cancel();
        }

        const session = appEnvironment.uploadAppFile?.(file, "image", this.reportProgress(index));
        if (session === undefined) return;
        this.uploadSessions[index] = session;

        let uploadURL: string | undefined;
        while (true) {
            const response = await session.attempt();
            if (!isUploadFileResponseError(response)) {
                uploadURL = response.path;
                break;
            }
            if (!(await showUploadFailedRetryModal(response, appEnvironment.appKind))) break;
        }

        onUploadEnd(index, uploadURL);
        if (uploadURL !== undefined) {
            this.setSourceOverride(uploadURL, index);
        }
        if (this.uploadSessions[index] === session) {
            delete this.uploadSessions[index];
        }
    }

    private onDropImageHandler = (imgHandle: string, data: DropData) => {
        const { onUploadStart, onUploadEnd } = this.props;
        const { index } = this.state;

        extractDropData(imgHandle);

        let askedToRemove = false;
        let readAsURLPromise = Promise.resolve("");

        const setURLOverride = (url: string) => {
            this.setSourceOverride(url, index);
            return url;
        };

        const clearURLOverride = () => {
            if (askedToRemove) return;
            askedToRemove = true;
            void readAsURLPromise.then((override: string) => {
                this.clearSourceOverride(override, index);
                this.clearUploadProgress(index);
            });
        };

        onUploadStart?.call(undefined, index);
        this.setUploadProgress(0, index);
        try {
            switch (data.kind) {
                case "uri":
                    readAsURLPromise = Promise.resolve(data.uri);
                    setURLOverride(data.uri);
                    onUploadEnd?.call(undefined, index, data.uri);
                    clearURLOverride();

                    break;
                case "file":
                    readAsURLPromise = readFileAsURL(data.file).then(setURLOverride);
                    void this.handleFileUpload(data.file, index).finally(clearURLOverride);

                    break;
            }
        } catch (e: unknown) {
            logError(e);
        }
    };

    private onOverlayClose = () => this.setState({ overlayShowing: false });

    private onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { onUploadStart, onUploadEnd } = this.props;
        const { index } = this.state;

        const firstFile = getFirstFileFromInput(event);
        if (firstFile === undefined || onUploadEnd === undefined) return;

        const { current } = this.inputRef;
        if (current !== null) {
            current.value = "";
        }

        let askedToRemove = false;
        let readAsURLPromise = Promise.resolve("");

        const clearURLOverride = () => {
            if (askedToRemove) return;
            askedToRemove = true;
            void readAsURLPromise.then((override: string) => {
                this.clearSourceOverride(override, index);
                this.clearUploadProgress(index);
            });
        };

        this.setUploadProgress(0, index);
        onUploadStart?.call(undefined, index);

        readAsURLPromise = readFileAsURL(firstFile).then(url => {
            this.setSourceOverride(url, index);
            return url;
        });

        void this.handleFileUpload(firstFile, index).finally(clearURLOverride);
    };

    private setIndex = (index: number) => {
        this.setState({ index });
    };

    public componentDidUpdate() {
        const { images } = this.props;
        // If we've "latched" to override values, we don't need to
        // keep them persistent in the override.
        for (const [idx, img] of images.entries()) {
            this.clearSourceOverride(img, idx);
        }
    }

    public componentWillUnmount() {
        // Cancel all pending uploads when the component unmounts.
        // Clearly, we don't care about them anymore.
        // FIXME: We should probably not be relying on React state to
        // do this...
        const sessions = { ...this.uploadSessions };
        for (const stringIndex of Object.keys(sessions)) {
            // Even though the type is Record<number, ...>,
            // the keys are still strings. JavaScript just converts
            // the numbers to strings before storing them as property
            // names.
            const index = stringIndex as unknown as number;
            const session = sessions[index];
            session.cancel();
            delete this.uploadSessions[index];
        }
    }

    public render() {
        const {
            size,
            images,
            sizing,
            gravity,
            fitStyle,
            bottomLeftOverlay,
            bottomRightOverlay,
            topLeftOverlay,
            topRightOverlay,
            onUploadEnd,
            uploadDisabled,
            showUploadButton,
        } = this.props;

        const { index, sourceOverrides, overlayShowing, uploadProgresses } = this.state;

        const alt = (
            <PlaceholderWrapper fitToContent={size === ImageAspectRatio.Fit}>
                <ImagePlaceholder />
            </PlaceholderWrapper>
        );

        const isSingleEmoji = images.length === 1 && isEmoji(images[0]);

        const children = images.map((i, n) => (
            <AutoImage
                fontSize={120}
                size={size}
                sizing={sizing}
                gravity={gravity}
                draggable={false}
                whenLoading={alt}
                key={n}
                src={sourceOverrides[n] ?? i}
                disabled={uploadProgresses[n] !== undefined}
            />
        ));

        let overlayNode: React.ReactNode;
        if (overlayShowing) {
            overlayNode = (
                <ErrorBoundary>
                    <React.Suspense fallback={<div />}>
                        <ImageOverlayView
                            hideOverlay={this.onOverlayClose}
                            resizedImgSrc={""}
                            imgSrc={massageCloudURL(images[index], false)}
                        />
                    </React.Suspense>
                </ErrorBoundary>
            );
        }

        const canUpload = onUploadEnd !== undefined && showUploadButton === true;
        const uploading = uploadProgresses[index] !== undefined;

        const uploadNode = canUpload ? (
            <label className="carousel-upload-button">
                <input
                    type="file"
                    accept="image/*"
                    disabled={uploadDisabled || uploading}
                    onChange={this.onInputChange}
                    ref={this.inputRef}
                />
                {uploading ? (
                    <div className={"upload-container"} onClick={this.onUploadCancelClick}>
                        {uploadCancelX()}
                    </div>
                ) : (
                    <StreamlineCamera />
                )}
            </label>
        ) : undefined;

        let klass = fitStyle === ImageFitStyle.Content ? "inset" : "";

        if (isSingleEmoji) {
            klass += " single-emoji";
        }

        if (this.props.onClickItem !== undefined) {
            klass += " interactive";
        }

        const currentUploadProgress = uploadProgresses[index];

        return (
            <ImageCarouselStyle data-test="app-image-carousel" className={klass}>
                <DroppableDiv
                    allowDrop={onUploadEnd !== undefined}
                    onDropImage={this.onDropImageHandler}
                    onClick={this.onClick}>
                    <Carousel fitStyle={fitStyle} index={index} onIndexChange={this.setIndex} size={size}>
                        {children}
                    </Carousel>
                </DroppableDiv>
                <OverlayContainerView
                    className="carousel-overlay"
                    bottomLeft={bottomLeftOverlay}
                    bottomRight={uploadNode ?? bottomRightOverlay}
                    topLeft={topLeftOverlay}
                    topRight={topRightOverlay}
                />
                {overlayNode}
                {currentUploadProgress !== undefined ? (
                    <ImageCarouselProgressBar progress={currentUploadProgress} />
                ) : null}
            </ImageCarouselStyle>
        );
    }
}

export default ImageCarousel;
